import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { companyService } from "@/features/company/services/companyService";
import { Button } from "@/shared/components/ui/Button";
import { Input } from "@/shared/components/ui/Input";
import { Modal } from "@/shared/components/ui/Modal";
import { Spinner } from "@/shared/components/ui/Spinner";
import { Toast } from "@/shared/components/feedback/Toast";
import { RegistrationCodesModal } from "@/features/company/components/RegistrationCodesModal";

const OFFICER_TYPES = ["officer", "admin"];

interface CompanyRow {
  company_id: string;
  company_name: { en: string; ja?: string };
  company_code?: string;
  website_url?: string;
  is_active: boolean;
  ticketing: boolean;
  flight_tracker: boolean;
  company_form: boolean;
  sort_order: number;
  coordinators?: string[];
}

interface CoordinatorOption {
  value: string;
  label: string;
}

interface FormState {
  name_en: string;
  name_ja: string;
  company_code: string;
  website_url: string;
  sort_order: string;
  is_active: boolean;
  ticketing: boolean;
  flight_tracker: boolean;
  company_form: boolean;
  coordinatorIds: string[];
}

const INIT_FORM: FormState = {
  name_en: "",
  name_ja: "",
  company_code: "",
  website_url: "",
  sort_order: "0",
  is_active: true,
  ticketing: false,
  flight_tracker: false,
  company_form: false,
  coordinatorIds: [],
};

const displayName = (c: CompanyRow) => c.company_name?.en || "—";

export const CompanyPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { userData, roles } = useAuth();
  const userType = userData?.userType?.toUpperCase() || "";
  const isOfficer = OFFICER_TYPES.includes(userData?.userType?.toLowerCase() || "");
  const canWrite =
    userType === "ADMIN" ||
    (isOfficer && roles.length === 0) ||
    roles.includes("company_write");

  const [companies, setCompanies] = useState<CompanyRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const filteredCompanies = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return companies;
    return companies.filter(
      (c) =>
        c.company_name?.en?.toLowerCase().includes(q) ||
        c.company_name?.ja?.toLowerCase().includes(q) ||
        c.website_url?.toLowerCase().includes(q),
    );
  }, [companies, search]);

  // Form modal
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(INIT_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  // Registration codes modal
  const [codesTarget, setCodesTarget] = useState<CompanyRow | null>(null);

  // Delete modal
  const [deleteTarget, setDeleteTarget] = useState<CompanyRow | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteBlockMsg, setDeleteBlockMsg] = useState<string | null>(null);

  const [coordinatorOptions, setCoordinatorOptions] = useState<CoordinatorOption[]>([]);
  const [coordSearch, setCoordSearch] = useState("");

  const [toast, setToast] = useState<{ message: string; type: "success" | "error" } | null>(null);
  const showToast = useCallback((message: string, type: "success" | "error") => {
    setToast({ message, type });
  }, []);

  const loadCompanies = useCallback(async () => {
    try {
      setLoading(true);
      const data = await companyService.getAll();
      setCompanies(data);
    } catch {
      showToast(t("company.load_error"), "error");
    } finally {
      setLoading(false);
    }
  }, [showToast, t]);

  useEffect(() => {
    if (isOfficer) {
      loadCompanies();
      companyService.getCoordinatorOptions().then(setCoordinatorOptions).catch(console.error);
    } else {
      setLoading(false);
    }
  }, [isOfficer, loadCompanies]);


  const openCreate = () => {
    setEditingId(null);
    setForm(INIT_FORM);
    setCoordSearch("");
    setFormError("");
    setModalOpen(true);
  };

  const openEdit = (company: CompanyRow) => {
    setEditingId(company.company_id);
    setForm({
      name_en: company.company_name?.en || "",
      name_ja: company.company_name?.ja || "",
      company_code: company.company_code || "",
      website_url: company.website_url || "",
      sort_order: String(company.sort_order ?? 0),
      is_active: company.is_active ?? true,
      ticketing: company.ticketing ?? false,
      flight_tracker: company.flight_tracker ?? false,
      company_form: company.company_form ?? false,
      coordinatorIds: company.coordinators ?? [],
    });
    setCoordSearch("");
    setFormError("");
    setModalOpen(true);
  };

  const closeModal = () => {
    if (!saving) setModalOpen(false);
  };

  const exportCSV = () => {
    const escapeCSV = (val: string) => {
      if (val.includes(",") || val.includes('"') || val.includes("\n")) {
        return `"${val.replace(/"/g, '""')}"`;
      }
      return val;
    };

    const headers = [
      t("company.company_code"),
      t("company.name_en"),
      t("company.name_ja"),
      t("company.website"),
      t("company.sort_order"),
      t("company.is_active"),
      t("company.ticketing"),
      t("company.flight_tracker"),
      t("company.company_form"),
    ];

    const rows = filteredCompanies.map((c) => [
      escapeCSV(c.company_code || ""),
      escapeCSV(c.company_name?.en || ""),
      escapeCSV(c.company_name?.ja || ""),
      escapeCSV(c.website_url || ""),
      String(c.sort_order ?? 0),
      c.is_active ? "Yes" : "No",
      c.ticketing ? "Yes" : "No",
      c.flight_tracker ? "Yes" : "No",
      c.company_form ? "Yes" : "No",
    ]);

    const csvContent = [headers.map(escapeCSV).join(","), ...rows.map((r) => r.join(","))].join("\n");
    const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `companies_${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleSave = async () => {
    setFormError("");
    if (!form.name_en.trim()) {
      setFormError(t("company.name_required"));
      return;
    }

    const payload = {
      company_name: {
        en: form.name_en.trim(),
        ...(form.name_ja.trim() ? { ja: form.name_ja.trim() } : {}),
      },
      website_url: form.website_url.trim() || undefined,
      sort_order: parseInt(form.sort_order, 10) || 0,
      is_active: form.is_active,
      ticketing: form.ticketing,
      flight_tracker: form.flight_tracker,
      company_form: form.company_form,
      coordinators: form.coordinatorIds.length > 0 ? form.coordinatorIds : undefined,
    };

    setSaving(true);
    try {
      if (editingId) {
        await companyService.update(editingId, payload);
      } else {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        await companyService.create(payload as any);
      }
      showToast(t("company.save_success"), "success");
      setModalOpen(false);
      await loadCompanies();
    } catch {
      setFormError(t("company.save_error"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await companyService.delete(deleteTarget.company_id);
      showToast(t("company.delete_success"), "success");
      setDeleteTarget(null);
      await loadCompanies();
    } catch (err: unknown) {
      const apiError = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      if (apiError === "company_has_users") {
        setDeleteBlockMsg(t("company.delete_blocked"));
      } else {
        showToast(t("company.delete_error"), "error");
        setDeleteTarget(null);
      }
    } finally {
      setDeleting(false);
    }
  };

  // ── Permission guard ─────────────────────────────────────────────────────

  if (!isOfficer) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <svg className="w-12 h-12 text-gray-300 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
        <p className="text-sm text-gray-500">{t("common.permission_denied")}</p>
      </div>
    );
  }

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div>
      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}

      {/* Sticky Header */}
      <div className="sticky top-0 z-20 bg-gray-50 -mx-3 px-3 md:-mx-6 md:px-6 py-3 mb-4 md:mb-6 border-b border-gray-200">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/other-setup")}
              className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div>
            <h1 className="text-lg md:text-xl font-bold text-gray-900">{t("company.title")}</h1>
            {!loading && (
              <p className="text-xs text-gray-400">
                {search.trim()
                  ? `${filteredCompanies.length} / ${companies.length} ${t("company.count_label")}`
                  : `${companies.length} ${t("company.count_label")}`}
              </p>
            )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="secondary" onClick={exportCSV}>
              <svg className="w-4 h-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              {t("company.export_csv")}
            </Button>
            {canWrite && (
              <Button size="sm" onClick={openCreate}>
                {t("company.new_company")}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Company List */}
      <div>
        <div className="bg-white rounded-xl border border-gray-200">
          {/* Search bar */}
          {!loading && companies.length > 0 && (
            <div className="px-4 md:px-6 py-3 border-b border-gray-100">
              <div className="relative">
                <svg
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none"
                  fill="none" viewBox="0 0 24 24" stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 115 11a6 6 0 0112 0z" />
                </svg>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t("common.search_e")}
                  className="w-full pl-9 pr-8 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-gray-50"
                />
                {search && (
                  <button
                    type="button"
                    onClick={() => setSearch("")}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          )}

          {loading ? (
            <div className="py-10">
              <Spinner />
            </div>
          ) : companies.length === 0 ? (
            <div className="text-center py-12 text-sm text-gray-400">
              <svg className="w-10 h-10 mx-auto mb-3 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              {t("company.no_companies")}
            </div>
          ) : filteredCompanies.length === 0 ? (
            <div className="text-center py-12 text-sm text-gray-400">
              <svg className="w-10 h-10 mx-auto mb-3 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-4.35-4.35M17 11A6 6 0 115 11a6 6 0 0112 0z" />
              </svg>
              {t("company.no_search_results")}
            </div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {filteredCompanies.map((company) => (
                <li key={company.company_id} className="flex items-start gap-3 px-4 md:px-6 py-4">
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <p className="text-sm font-semibold text-gray-900">{displayName(company)}</p>
                      {company.company_name?.ja && (
                        <p className="text-xs text-gray-400">{company.company_name.ja}</p>
                      )}
                      {company.company_code && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-mono font-medium bg-gray-100 text-gray-600">
                          {company.company_code}
                        </span>
                      )}
                      {(company.sort_order ?? 0) > 0 && (
                        <span className="text-[10px] text-gray-400 font-mono">
                          #{company.sort_order}
                        </span>
                      )}
                    </div>

                    {company.website_url && (
                      <a
                        href={company.website_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-indigo-500 hover:text-indigo-700 truncate block mt-0.5"
                      >
                        {company.website_url}
                      </a>
                    )}

                    {/* Status + feature badges */}
                    <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                      <span
                        className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${
                          company.is_active
                            ? "bg-green-50 text-green-700"
                            : "bg-gray-100 text-gray-500"
                        }`}
                      >
                        {company.is_active ? t("settings.status_active") : t("settings.status_inactive")}
                      </span>
                      {company.ticketing && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-indigo-50 text-indigo-600">
                          {t("company.ticketing")}
                        </span>
                      )}
                      {company.flight_tracker && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-indigo-50 text-indigo-600">
                          {t("company.flight_tracker")}
                        </span>
                      )}
                      {company.company_form && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-indigo-50 text-indigo-600">
                          {t("company.company_form")}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 shrink-0 pt-0.5">
                    <Button size="sm" variant="secondary" onClick={() => setCodesTarget(company)}>
                      {t("company.codes.button")}
                    </Button>
                    {canWrite && (
                      <Button size="sm" variant="secondary" onClick={() => openEdit(company)}>
                        {t("common.edit")}
                      </Button>
                    )}
                    {canWrite && (
                      <button
                        type="button"
                        onClick={() => { setDeleteBlockMsg(null); setDeleteTarget(company); }}
                        className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        title={t("common.delete")}
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Create / Edit Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={closeModal}
        title={editingId ? t("company.edit_company") : t("company.new_company")}
        headerAction={
          canWrite ? (
            <Button size="sm" onClick={handleSave} loading={saving}>
              {t("common.save")}
            </Button>
          ) : undefined
        }
      >
        <div className="space-y-4">
          {/* Names */}
          <Input
            label={t("company.name_en")}
            placeholder={t("company.name_en_placeholder")}
            value={form.name_en}
            onChange={(e) => setForm((f) => ({ ...f, name_en: e.target.value }))}
          />
          <Input
            label={t("company.name_ja")}
            placeholder={t("company.name_ja_placeholder")}
            value={form.name_ja}
            onChange={(e) => setForm((f) => ({ ...f, name_ja: e.target.value }))}
          />
          <Input
            label={t("company.company_code")}
            value={form.company_code || (editingId ? "" : t("company.company_code_auto"))}
            readOnly
            className="bg-gray-50 text-gray-400 cursor-default"
          />

          {/* Website + Sort order */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label={t("company.website")}
              placeholder={t("company.website_placeholder")}
              value={form.website_url}
              onChange={(e) => setForm((f) => ({ ...f, website_url: e.target.value }))}
            />
            <Input
              label={t("company.sort_order")}
              type="number"
              min={0}
              value={form.sort_order}
              onChange={(e) => setForm((f) => ({ ...f, sort_order: e.target.value }))}
            />
          </div>

          {/* Toggles */}
          <div>
            <p className="block text-sm font-medium text-gray-700 mb-2">
              {t("company.features")}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-4">
              {(
                [
                  { key: "is_active", label: t("company.is_active") },
                  { key: "ticketing", label: t("company.ticketing") },
                  { key: "flight_tracker", label: t("company.flight_tracker") },
                  { key: "company_form", label: t("company.company_form") },
                ] as const
              ).map(({ key, label }) => (
                <label key={key} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form[key]}
                    onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.checked }))}
                    className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  {label}
                </label>
              ))}
            </div>
          </div>

          {/* Coordinator multi-select */}
          <div>
            <p className="block text-sm font-medium text-gray-700 mb-2">{t("company.coordinator")}</p>

            {/* Selected tags */}
            {form.coordinatorIds.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2">
                {form.coordinatorIds.map((id) => {
                  const opt = coordinatorOptions.find((o) => o.value === id);
                  return (
                    <span key={id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">
                      {opt?.label ?? id}
                      <button
                        type="button"
                        onClick={() => setForm((f) => ({ ...f, coordinatorIds: f.coordinatorIds.filter((x) => x !== id) }))}
                        className="text-indigo-400 hover:text-indigo-600"
                      >
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </span>
                  );
                })}
              </div>
            )}

            {/* Search filter */}
            <input
              type="text"
              value={coordSearch}
              onChange={(e) => setCoordSearch(e.target.value)}
              placeholder={t("company.coordinator_placeholder")}
              className="w-full px-3 py-2 mb-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />

            {/* Always-visible scrollable list */}
            <div className="border border-gray-200 rounded-lg max-h-36 overflow-y-auto">
              {(() => {
                const q = coordSearch.toLowerCase().trim();
                const filtered = coordinatorOptions.filter(
                  (o) => q === "" || o.label.toLowerCase().includes(q),
                );
                if (filtered.length === 0) {
                  return (
                    <div className="px-3 py-3 text-sm text-gray-400 text-center">
                      {t("leave.user_no_results")}
                    </div>
                  );
                }
                return filtered.map((o) => {
                  const selected = form.coordinatorIds.includes(o.value);
                  return (
                    <button
                      key={o.value}
                      type="button"
                      onClick={() =>
                        setForm((f) => ({
                          ...f,
                          coordinatorIds: selected
                            ? f.coordinatorIds.filter((x) => x !== o.value)
                            : [...f.coordinatorIds, o.value],
                        }))
                      }
                      className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 transition-colors ${
                        selected ? "bg-indigo-50 text-indigo-700 font-medium" : "text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${
                        selected ? "bg-indigo-600 border-indigo-600" : "border-gray-300"
                      }`}>
                        {selected && (
                          <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </span>
                      {o.label}
                    </button>
                  );
                });
              })()}
            </div>
          </div>

          {formError && <p className="text-sm text-red-600">{formError}</p>}
        </div>
      </Modal>

      {/* Registration Codes Modal */}
      {codesTarget && (
        <RegistrationCodesModal
          companyId={codesTarget.company_id}
          companyName={codesTarget.company_name}
          onClose={() => setCodesTarget(null)}
        />
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={!!deleteTarget}
        onClose={() => { if (!deleting) { setDeleteTarget(null); setDeleteBlockMsg(null); } }}
        title={t("common.confirm_delete")}
      >
        <div className="space-y-4">
          {deleteTarget && (
            <p className="text-sm font-semibold text-gray-900 bg-gray-50 rounded-lg px-3 py-2">
              {displayName(deleteTarget)}
            </p>
          )}

          {deleteBlockMsg ? (
            /* Blocked state — company has users */
            <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <svg className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
              </svg>
              <p className="text-sm text-amber-800">{deleteBlockMsg}</p>
            </div>
          ) : (
            <p className="text-sm text-gray-600">{t("company.confirm_delete")}</p>
          )}

          <div className="flex gap-3 justify-end pt-1">
            <Button
              variant="secondary"
              onClick={() => { setDeleteTarget(null); setDeleteBlockMsg(null); }}
              disabled={deleting}
            >
              {t("common.cancel")}
            </Button>
            <Button
              variant="danger"
              onClick={handleDelete}
              loading={deleting}
              disabled={!!deleteBlockMsg}
            >
              {t("common.delete")}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
